import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';

/**
 * Mobile-friendly qty stepper.
 *
 * Uses `type="text" inputMode="numeric"` (not `type="number"`) so:
 *   1) The user can transiently empty the field to retype a value —
 *      the raw string is buffered in local component state and only
 *      coerced to a number on blur. `type="number"` combined with our
 *      snap-to-min-1 logic made this impossible on iOS/Android
 *      (deleting "1" → blank → NaN → snap back to 1 before the next
 *      digit could land).
 *   2) Mobile keyboards show the numeric keypad without spinners.
 *   3) Explicit − / + buttons flank the input for one-thumb use.
 */
const QtyStepper = ({ idx, qty, disabled, onChangeQty }) => {
  const [raw, setRaw] = useState(String(qty));
  // Sync when the parent qty changes (e.g. via step buttons).
  useEffect(() => { setRaw(String(qty)); }, [qty]);

  const commit = () => {
    const parsed = Math.max(1, parseInt(raw, 10) || 1);
    if (parsed !== qty) onChangeQty(idx, parsed);
    setRaw(String(parsed));
  };

  return (
    <div className="flex items-center gap-0.5">
      <Label htmlFor={`part-qty-${idx}`} className="text-xs mr-1">Qty</Label>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChangeQty(idx, Math.max(1, qty - 1))}
        disabled={disabled || qty <= 1}
        aria-label="Decrease quantity"
        data-testid={`part-qty-dec-${idx}`}
        className="h-9 w-9 p-0 text-lg leading-none touch-manipulation"
      >
        −
      </Button>
      <Input
        id={`part-qty-${idx}`}
        data-testid={`selected-part-qty-${idx}`}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={raw}
        onChange={(e) => {
          // Allow only digits (or empty) during editing.
          const v = e.target.value.replace(/[^0-9]/g, '');
          setRaw(v);
        }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); } }}
        onFocus={(e) => e.target.select()}
        className="w-14 h-9 text-center text-base"
        disabled={disabled}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onChangeQty(idx, qty + 1)}
        disabled={disabled}
        aria-label="Increase quantity"
        data-testid={`part-qty-inc-${idx}`}
        className="h-9 w-9 p-0 text-lg leading-none touch-manipulation"
      >
        +
      </Button>
    </div>
  );
};

/**
 * PartsPicker — inventory-aware parts selection for maintenance logs.
 *
 * Props:
 *   value      — [{ part_number, name, qty, isNew?, existingQty? }]
 *   onChange   — setter
 *   disabled   — bool
 *
 * Users search their inventory by part number or name and pick parts;
 * if a search term has no match they can add it as a brand-new inventory
 * item (created at qty 0, no deduction). Selected parts appear as chips
 * with editable qty and a remove button.
 */
const PartsPicker = ({ value = [], onChange, disabled = false, onSelectSerialized }) => {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newPart, setNewPart] = useState({ part_number: '', name: '' });
  const containerRef = useRef(null);

  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/inventory`);
        if (!aborted) setInventory(res.data || []);
      } catch (e) {
        // Non-fatal — user can still add custom parts.
        if (!aborted) setInventory([]);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const selectedPartNumbers = useMemo(
    () => new Set(value.map((v) => v.part_number)),
    [value]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return inventory
      .filter((it) => {
        if (selectedPartNumbers.has(it.part_number)) return false;
        const pn = (it.part_number || '').toLowerCase();
        const nm = (it.name || '').toLowerCase();
        return pn.includes(q) || nm.includes(q);
      })
      .slice(0, 8);
  }, [query, inventory, selectedPartNumbers]);

  const addPart = (item) => {
    const next = [
      ...value,
      {
        part_number: item.part_number,
        name: item.name,
        qty: 1,
        existingQty: item.quantity ?? 0,
      },
    ];
    onChange(next);
    setQuery('');
    setShowDropdown(false);
  };

  const addNewPart = () => {
    const partNumber = newPart.part_number.trim();
    const name = newPart.name.trim();
    if (!partNumber || !name) return;
    if (selectedPartNumbers.has(partNumber)) {
      setNewPart({ part_number: '', name: '' });
      setShowNewForm(false);
      return;
    }
    onChange([
      ...value,
      {
        part_number: partNumber,
        name,
        qty: 1,
        isNew: true,
      },
    ]);
    setNewPart({ part_number: '', name: '' });
    setShowNewForm(false);
    setQuery('');
    setShowDropdown(false);
  };

  const removePart = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const updateQty = (idx, qty) => {
    // Clamp final value to >= 1. Callers only invoke this with a fully-formed
    // number (blur / step buttons) so it's safe to snap here.
    const parsed = Math.max(1, parseInt(qty, 10) || 1);
    onChange(value.map((p, i) => (i === idx ? { ...p, qty: parsed } : p)));
  };

  return (
    <div className="space-y-2" ref={containerRef} data-testid="parts-picker">
      {/* Selected parts */}
      {value.length > 0 && (
        <div className="space-y-2 border rounded-md p-2 bg-slate-50">
          {value.map((p, idx) => {
            const willGoNegative = !p.isNew && (p.existingQty ?? 0) - p.qty < 0;
            return (
              <div
                key={`${p.part_number}-${idx}`}
                data-testid={`selected-part-${idx}`}
                className="flex items-center gap-2 bg-white border rounded px-2 py-1.5 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-medium text-slate-900">{p.part_number}</span>
                    {p.isNew && (
                      <span className="text-[10px] uppercase font-semibold bg-blue-100 text-blue-800 rounded px-1.5 py-0.5">
                        New
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 truncate">{p.name}</div>
                  {!p.isNew && (
                    <div className="text-[11px] text-slate-500">
                      In stock: {p.existingQty} → after: {Math.max(0, (p.existingQty ?? 0) - p.qty)}
                      {willGoNegative && (
                        <span className="ml-2 text-red-700 font-medium">⚠ insufficient stock</span>
                      )}
                    </div>
                  )}
                  {p.isNew && (
                    <div className="text-[11px] text-blue-700">
                      Will be created in inventory at qty 0 (no deduction)
                    </div>
                  )}
                </div>
                <QtyStepper idx={idx} qty={p.qty} disabled={disabled} onChangeQty={updateQty} />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePart(idx)}
                  disabled={disabled}
                  data-testid={`remove-part-${idx}`}
                  className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
                  aria-label="Remove part"
                >
                  ×
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search input */}
      {!showNewForm && (
        <div className="relative">
          <Input
            data-testid="parts-search-input"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            placeholder={loading ? 'Loading inventory…' : 'Search inventory by part # or name'}
            disabled={disabled || loading}
          />
          {showDropdown && query.trim() && (
            <div
              className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-64 overflow-y-auto"
              data-testid="parts-search-dropdown"
            >
              {matches.length > 0 ? (
                matches.map((it) => (
                  <div key={it.id}>
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => addPart(it)}
                    data-testid={`parts-search-result-${it.part_number}`}
                    className="w-full text-left px-3 py-2 hover:bg-slate-100 border-b last:border-b-0"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-mono text-sm font-medium">{it.part_number}</span>
                      <span className="text-xs text-slate-500" data-testid={`bulk-stock-${it.id}`}>Bulk stock: {it.quantity}</span>
                    </div>
                    <div className="text-xs text-slate-600 truncate">{it.name}</div>
                  </button>
                  {onSelectSerialized && it.serialized_total > 0 && <button type="button"
                    disabled={disabled} data-testid={`choose-serials-${it.id}`}
                    onClick={() => { onSelectSerialized(it.part_number); setQuery(''); setShowDropdown(false); }}
                    className="w-full px-3 py-2 text-left text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors">
                    Choose serialized parts · {it.serialized_installable || 0} in inventory · {it.serialized_installed || 0} installed
                  </button>}
                  </div>
                ))
              ) : (
                <div className="px-3 py-3 text-sm text-slate-600">
                  No matches for <span className="font-mono">&quot;{query}&quot;</span>.
                  <button
                    type="button"
                    onClick={() => {
                      setNewPart({ part_number: query.trim(), name: '' });
                      setShowNewForm(true);
                      setShowDropdown(false);
                    }}
                    data-testid="parts-add-new-btn"
                    className="ml-2 text-blue-600 hover:text-blue-800 font-medium underline"
                  >
                    Add as new part
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New part form */}
      {showNewForm && (
        <div className="border rounded-md p-3 bg-blue-50 space-y-2" data-testid="parts-new-form">
          <div className="text-sm font-medium text-blue-900">Add new inventory part</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label htmlFor="new-part-number" className="text-xs">Part Number *</Label>
              <Input
                id="new-part-number"
                data-testid="new-part-number-input"
                value={newPart.part_number}
                onChange={(e) => setNewPart({ ...newPart, part_number: e.target.value })}
                placeholder="e.g. CAL-9001"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-part-name" className="text-xs">Name *</Label>
              <Input
                id="new-part-name"
                data-testid="new-part-name-input"
                value={newPart.name}
                onChange={(e) => setNewPart({ ...newPart, name: e.target.value })}
                placeholder="e.g. Caleffi 9001 Air Vent"
              />
            </div>
          </div>
          <p className="text-xs text-blue-800">
            Will be created in inventory at qty 0. Update stock later from the Inventory page.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setShowNewForm(false);
                setNewPart({ part_number: '', name: '' });
              }}
              data-testid="new-part-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={addNewPart}
              disabled={!newPart.part_number.trim() || !newPart.name.trim()}
              data-testid="new-part-add-btn"
            >
              Add Part
            </Button>
          </div>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Bulk quantities are deducted on save. Serialized parts are selected separately.
      </p>
    </div>
  );
};

export default PartsPicker;
